import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { agenciesApi, clientsApi, discountsApi, commissionsApi, Agency, Client } from '../services/api';
import { extractErrorMessage as _extractErrorMessage } from '../utils/errorMessage';
import '../styles/agencies.css';
import { getBranding } from '../config/branding';
import { Trans, useTranslation } from 'react-i18next';
import i18n, { currentLocale } from '../i18n';
import { RequisitesFields, Requisites, EMPTY_REQUISITES } from './RequisitesFields';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface Office {
  id: number;
  name: string;
  code: string;
  currency?: string;
}

interface Manager {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

// Local helper that fixes the default fallback for callers in this file.
const extractErrorMessage = (err: any): string => _extractErrorMessage(err, i18n.t('agencies.errGeneric'));

// ============================================
// Create Agency Modal
// ============================================

const CreateAgencyModal: React.FC<{
  offices: Office[];
  onClose: () => void;
  onSuccess: () => void;
  getAuthHeaders: () => Record<string, string>;
  currentUser?: { role?: string; regional_office_id?: number | null; regional_office_name?: string | null } | null;
}> = ({ offices, onClose, onSuccess, getAuthHeaders, currentUser }) => {
  const { t } = useTranslation();
  const isRegionalAdmin = currentUser?.role === 'regional_admin';
  const [name, setName] = useState('');
  const [officeId, setOfficeId] = useState<number | undefined>(
    isRegionalAdmin && currentUser?.regional_office_id ? currentUser.regional_office_id : undefined
  );
  const [managerId, setManagerId] = useState<number | undefined>();
  const [managers, setManagers] = useState<Manager[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [defaultClientMarkup, setDefaultClientMarkup] = useState<string>('0');
  const [billingModel, setBillingModel] = useState<string>('prepaid');
  const [requisites, setRequisites] = useState<Requisites>(EMPTY_REQUISITES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchManagers = async (oId: number, autoSelectDefault?: boolean) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/clients/managers?office_id=${oId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setManagers(data.managers || []);
        // Auto-select default manager from office
        if (autoSelectDefault && data.default_manager_id) {
          setManagerId(data.default_manager_id);
        }
      }
    } catch { setManagers([]); }
  };

  const fetchClients = async (oId: number) => {
    try {
      const data = await clientsApi.list({ regional_office_id: oId, limit: 500 });
      // Show only clients not assigned to any agency
      setClients((data.clients || []).filter(c => !c.agency_id));
    } catch { setClients([]); }
  };

  // Auto-load managers/clients when office is set
  useEffect(() => {
    if (officeId) {
      fetchManagers(officeId, true);
      fetchClients(officeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOfficeChange = (value: string) => {
    const newOfficeId = value ? Number(value) : undefined;
    setOfficeId(newOfficeId);
    setManagerId(undefined);
    setManagers([]);
    setClients([]);
    setSelectedClientIds([]);
    if (newOfficeId) {
      fetchManagers(newOfficeId, true);
      fetchClients(newOfficeId);
    }
  };

  const toggleClient = (advId: string) => {
    setSelectedClientIds(prev =>
      prev.includes(advId) ? prev.filter(id => id !== advId) : [...prev, advId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('agencies.errNameRequired'));
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await agenciesApi.create({
        name: name.trim(),
        regional_office_id: officeId,
        manager_user_id: managerId,
        client_ids: selectedClientIds.length > 0 ? selectedClientIds : undefined,
        default_client_markup: parseFloat(defaultClientMarkup) || 0,
        billing_model: billingModel,
        ...requisites,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div className="offices-modal" onClick={e => e.stopPropagation()}>
        <div className="offices-modal-header">
          <h2>{t('agencies.createTitle')}</h2>
          <button className="offices-modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="offices-modal-body">
            {error && <div className="offices-modal-error">{error}</div>}

            <div className="offices-form-field">
              <label>{t('agencies.nameReq')}</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('agencies.namePh')}
                required
              />
            </div>

            {!isRegionalAdmin && (
              <div className="offices-form-field">
                <label>{t('clients.regionalOffice')}</label>
                <select value={officeId || ''} onChange={e => handleOfficeChange(e.target.value)}>
                  <option value="">{t('agencies.notAssigned')}</option>
                  {offices.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            )}

            {officeId && (
              <>
                <div className="offices-form-field">
                  <label>{t('agencies.brandManager', { brand: getBranding().brand_name })}</label>
                  <select value={managerId || ''} onChange={e => setManagerId(e.target.value ? Number(e.target.value) : undefined)}>
                    <option value="">{t('agencies.notAssigned')}</option>
                    {managers.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.first_name && m.last_name ? `${m.first_name} ${m.last_name}` : m.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="offices-form-field">
                  <label>{t('agencies.clientsSelected', { count: selectedClientIds.length })}</label>
                  <div className="users-advertisers-list">
                    {clients.length === 0 ? (
                      <div className="agencies-clients-empty">{t('agencies.noClientsInOffice')}</div>
                    ) : (
                      clients.map(c => (
                        <div
                          key={c.advertiser_id}
                          className={`users-advertiser-item ${selectedClientIds.includes(c.advertiser_id) ? 'selected' : ''}`}
                          onClick={() => toggleClient(c.advertiser_id)}
                        >
                          <span className="users-checkbox-custom">
                            <svg viewBox="0 0 24 24" fill="none">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                          <span className="users-advertiser-name">{c.display_name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="offices-form-field">
              <label>{t('agencies.defaultMarkup')}</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={defaultClientMarkup}
                onChange={e => setDefaultClientMarkup(e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="offices-form-field">
              <label>{t('clients.billingModel')}</label>
              <select value={billingModel} onChange={e => setBillingModel(e.target.value)}>
                <option value="prepaid">{t('billing.bm_prepaid')}</option>
                <option value="postpaid">{t('billing.bm_postpaid')}</option>
              </select>
            </div>

            {offices.find(o => o.id === officeId)?.currency === 'KZT' && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('clients.legalDetails')}</div>
                <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 8 }}>
                  {t('agencies.kzNote')}
                </div>
                <RequisitesFields value={requisites} onChange={setRequisites} />
              </div>
            )}
          </div>
          <div className="offices-modal-footer">
            <button type="button" className="offices-btn offices-btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="offices-btn offices-btn-primary" disabled={isSubmitting}>
              {t(isSubmitting ? 'agencies.creating' : 'agencies.createAgency')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// Edit Agency Modal
// ============================================

const EditAgencyModal: React.FC<{
  agency: Agency;
  offices: Office[];
  onClose: () => void;
  onSuccess: () => void;
  getAuthHeaders: () => Record<string, string>;
  currentUser?: { role?: string; regional_office_id?: number | null; regional_office_name?: string | null } | null;
}> = ({ agency, offices, onClose, onSuccess, getAuthHeaders, currentUser }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(agency.name);
  const [officeId, setOfficeId] = useState<number | undefined>(agency.regional_office_id || undefined);
  const [managerId, setManagerId] = useState<number | undefined>(agency.manager_user_id || undefined);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [defaultClientMarkup, setDefaultClientMarkup] = useState<string>(String(agency.default_client_markup || 0));
  const [billingModel, setBillingModel] = useState<string>(agency.billing_model || 'prepaid');
  const [requisites, setRequisites] = useState<Requisites>({
    legal_name: (agency as any).legal_name || '', tax_id: (agency as any).tax_id || '',
    legal_address: (agency as any).legal_address || '', contract_number: (agency as any).contract_number || '',
  });
  // Load full requisites (the list payload may omit them).
  useEffect(() => {
    agenciesApi.get(agency.id).then((a: any) => setRequisites({
      legal_name: a.legal_name || '', tax_id: a.tax_id || '',
      legal_address: a.legal_address || '', contract_number: a.contract_number || '',
    })).catch(() => {});
  }, [agency.id]);
  const isRegionalAdmin = currentUser?.role === 'regional_admin';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchManagers = async (oId: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/clients/managers?office_id=${oId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setManagers(data.managers || []);
      }
    } catch { setManagers([]); }
  };

  const fetchClients = async (oId: number, agencyId: number) => {
    try {
      const data = await clientsApi.list({ regional_office_id: oId, limit: 500 });
      // Show clients not assigned to any agency OR already assigned to this agency
      const available = (data.clients || []).filter(c => !c.agency_id || c.agency_id === agencyId);
      setClients(available);
      setSelectedClientIds(available.filter(c => c.agency_id === agencyId).map(c => c.advertiser_id));
    } catch { setClients([]); }
  };

  // Load managers and clients for current office on mount
  useEffect(() => {
    if (officeId) {
      fetchManagers(officeId);
      fetchClients(officeId, agency.id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOfficeChange = async (value: string) => {
    const newOfficeId = value ? Number(value) : undefined;
    setOfficeId(newOfficeId);
    setManagerId(undefined);
    setManagers([]);
    if (newOfficeId) {
      fetchManagers(newOfficeId);
      // Load clients from new office + keep current agency clients
      try {
        const [newOfficeData, currentAgencyData] = await Promise.all([
          clientsApi.list({ regional_office_id: newOfficeId, limit: 500 }),
          clientsApi.list({ agency_id: agency.id, limit: 500 }),
        ]);
        const newOfficeClients = (newOfficeData.clients || []).filter(c => !c.agency_id || c.agency_id === agency.id);
        const currentAgencyClients = (currentAgencyData.clients || []).filter(c => c.agency_id === agency.id);
        // Merge: current agency clients + available from new office (deduplicate)
        const seen = new Set<string>();
        const merged = [...currentAgencyClients, ...newOfficeClients].filter(c => {
          if (seen.has(c.advertiser_id)) return false;
          seen.add(c.advertiser_id);
          return true;
        });
        setClients(merged);
        setSelectedClientIds(currentAgencyClients.map(c => c.advertiser_id));
      } catch {
        setClients([]);
        setSelectedClientIds([]);
      }
    } else {
      setClients([]);
      setSelectedClientIds([]);
    }
  };

  const toggleClient = (advId: string) => {
    setSelectedClientIds(prev =>
      prev.includes(advId) ? prev.filter(id => id !== advId) : [...prev, advId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('agencies.errNameRequired'));
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await agenciesApi.update(agency.id, {
        name: name.trim(),
        regional_office_id: officeId,
        manager_user_id: managerId ?? 0,
        client_ids: selectedClientIds,
        default_client_markup: parseFloat(defaultClientMarkup) || 0,
        billing_model: billingModel,
        ...requisites,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div className="offices-modal" onClick={e => e.stopPropagation()}>
        <div className="offices-modal-header">
          <h2>{t('agencies.editTitle')}</h2>
          <button className="offices-modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="offices-modal-body">
            {error && <div className="offices-modal-error">{error}</div>}

            <div className="offices-form-field">
              <label>{t('agencies.nameReq')}</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>

            {!isRegionalAdmin && (
              <div className="offices-form-field">
                <label>{t('clients.regionalOffice')}</label>
                <select value={officeId || ''} onChange={e => handleOfficeChange(e.target.value)}>
                  <option value="">{t('agencies.notAssigned')}</option>
                  {offices.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            )}

            {officeId && (
              <>
                <div className="offices-form-field">
                  <label>{t('agencies.brandManager', { brand: getBranding().brand_name })}</label>
                  <select value={managerId || ''} onChange={e => setManagerId(e.target.value ? Number(e.target.value) : undefined)}>
                    <option value="">{t('agencies.notAssigned')}</option>
                    {managers.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.first_name && m.last_name ? `${m.first_name} ${m.last_name}` : m.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="offices-form-field">
                  <label>{t('agencies.clientsSelected', { count: selectedClientIds.length })}</label>
                  <div className="users-advertisers-list">
                    {clients.length === 0 ? (
                      <div className="agencies-clients-empty">{t('agencies.noClientsAvailable')}</div>
                    ) : (
                      clients.map(c => (
                        <div
                          key={c.advertiser_id}
                          className={`users-advertiser-item ${selectedClientIds.includes(c.advertiser_id) ? 'selected' : ''}`}
                          onClick={() => toggleClient(c.advertiser_id)}
                        >
                          <span className="users-checkbox-custom">
                            <svg viewBox="0 0 24 24" fill="none">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                          <span className="users-advertiser-name">{c.display_name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="offices-form-field">
              <label>{t('agencies.defaultMarkup')}</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={defaultClientMarkup}
                onChange={e => setDefaultClientMarkup(e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="offices-form-field">
              <label>{t('clients.billingModel')}</label>
              <select value={billingModel} onChange={e => setBillingModel(e.target.value)}>
                <option value="prepaid">{t('billing.bm_prepaid')}</option>
                <option value="postpaid">{t('billing.bm_postpaid')}</option>
              </select>
            </div>

            {offices.find(o => o.id === officeId)?.currency === 'KZT' && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('clients.legalDetails')}</div>
                <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 8 }}>
                  {t('agencies.kzNote')}
                </div>
                <RequisitesFields value={requisites} onChange={setRequisites} />
              </div>
            )}
          </div>
          <div className="offices-modal-footer">
            <button type="button" className="offices-btn offices-btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="offices-btn offices-btn-primary" disabled={isSubmitting}>
              {t(isSubmitting ? 'agencies.saving' : 'agencies.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// Discount Stages Modal
// ============================================

const DiscountStagesModal: React.FC<{
  agency: Agency;
  onClose: () => void;
}> = ({ agency, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newPercent, setNewPercent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const result = await discountsApi.getEntityDiscount('agency', agency.id);
      setData(result);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDate || !newPercent) return;
    setSubmitting(true);
    setError('');
    try {
      await discountsApi.addEntityStage('agency', agency.id, {
        discount_percent: parseFloat(newPercent),
        effective_from: newDate,
      });
      setNewDate('');
      setNewPercent('');
      await fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStage = async (stageId: number) => {
    try {
      await discountsApi.deleteEntityStage('agency', agency.id, stageId);
      await fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div className="offices-modal discount-stages-modal" onClick={e => e.stopPropagation()}>
        <div className="offices-modal-header">
          <h2>{t('agencies.discountTitle', { agency: agency.name })}</h2>
          <button className="offices-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="offices-modal-body">
          {error && <div className="offices-modal-error">{error}</div>}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div className="offices-spinner" />
            </div>
          ) : data && (
            <>
              {/* Current effective discount */}
              <div className="discount-current">
                <span className="discount-current-label">{t('agencies.currentDiscount')}</span>
                <span className={`discount-badge ${data.current_discount?.source === 'auto' ? 'discount-auto' : 'discount-manual'}`}>
                  {data.current_discount?.discount_percent || 0}%
                  <span className="discount-source">
                    {t(data.current_discount?.source === 'auto' ? 'agencies.sourceAuto' : 'agencies.sourceManual')}
                  </span>
                </span>
                {data.current_discount?.source === 'auto' && data.current_discount?.spend_3m != null && (
                  <span className="discount-spend-info">
                    {t('agencies.spend3m', { amount: Math.round(data.current_discount.spend_3m).toLocaleString(currentLocale()) })}
                  </span>
                )}
              </div>

              {/* Stages table */}
              {data.stages && data.stages.length > 0 && (
                <table className="users-table discount-stages-table" style={{ minWidth: 0, tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th>{t('agencies.colEffectiveFrom')}</th>
                      <th>{t('agencies.colDiscount')}</th>
                      <th>{t('agencies.colStatus')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.stages.map((stage: any) => (
                      <tr key={stage.id} className={stage.is_active ? 'stage-active' : 'stage-future'}>
                        <td>{stage.effective_from.split('-').reverse().join('.')}</td>
                        <td><strong>{stage.discount_percent}%</strong></td>
                        <td>
                          <span className={`discount-stage-status ${stage.is_active ? 'status-active' : 'status-future'}`}>
                            {t(stage.is_active ? 'agencies.stageActive' : 'agencies.stageFuture')}
                          </span>
                        </td>
                        <td>
                          <button
                            className="users-action-btn users-action-delete"
                            onClick={() => handleDeleteStage(stage.id)}
                            title={t('agencies.deleteStage')}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"/>
                              <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Add stage form */}
              <form onSubmit={handleAddStage} className="discount-add-form">
                <div className="discount-add-row">
                  <div className="discount-date-wrap">
                    <input
                      type="date"
                      value={newDate}
                      onChange={e => setNewDate(e.target.value)}
                      required
                      className="discount-input discount-date-native"
                    />
                    <span className="discount-date-display">
                      {newDate ? newDate.split('-').reverse().join('.') : <span style={{ color: 'var(--text-tertiary, #aaa)' }}>dd.mm.yyyy</span>}
                    </span>
                  </div>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    step={0.5}
                    value={newPercent}
                    onChange={e => setNewPercent(e.target.value)}
                    placeholder="0-50%"
                    required
                    className="discount-input"
                    style={{ width: '100px' }}
                  />
                  <button type="submit" className="offices-btn offices-btn-primary" disabled={submitting}>
                    {t(submitting ? 'agencies.adding' : 'agencies.add')}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
        <div className="offices-modal-footer">
          <button className="offices-btn offices-btn-secondary" onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Commission Stages Modal (mirrors Discount; adds Own/Client model toggle)
// ============================================

// `key` is the stored model_type — only labelKey is interface text.
const COMMISSION_MODELS: { key: string; labelKey: string }[] = [
  { key: 'own_accounts', labelKey: 'agencies.modelOwn' },
  { key: 'client_accounts', labelKey: 'agencies.modelClient' },
];

const CommissionStagesModal: React.FC<{
  agency: Agency;
  onClose: () => void;
}> = ({ agency, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newPercent, setNewPercent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const result = await commissionsApi.getEntityCommission('agency', agency.id);
      setData(result);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModelChange = async (model: string) => {
    setError('');
    try {
      await commissionsApi.setEntityModel('agency', agency.id, model);
      await fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleAddStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDate || !newPercent) return;
    setSubmitting(true);
    setError('');
    try {
      await commissionsApi.addEntityStage('agency', agency.id, {
        commission_percent: parseFloat(newPercent),
        effective_from: newDate,
      });
      setNewDate('');
      setNewPercent('');
      await fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStage = async (stageId: number) => {
    try {
      await commissionsApi.deleteEntityStage('agency', agency.id, stageId);
      await fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const cur = data?.current_commission;
  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div className="offices-modal discount-stages-modal" onClick={e => e.stopPropagation()}>
        <div className="offices-modal-header">
          <h2>{t('agencies.commissionTitle', { agency: agency.name })}</h2>
          <button className="offices-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="offices-modal-body">
          {error && <div className="offices-modal-error">{error}</div>}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}><div className="offices-spinner" /></div>
          ) : data && (
            <>
              {/* Account model toggle (determines which matrix drives the auto %) */}
              <div className="settings-form-group" style={{ marginBottom: 16 }}>
                <label className="field-label">{t('agencies.accountModel')}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {COMMISSION_MODELS.map(m => (
                    <button
                      key={m.key}
                      type="button"
                      className={`offices-btn ${data.model_type === m.key ? 'offices-btn-primary' : 'offices-btn-secondary'}`}
                      onClick={() => handleModelChange(m.key)}
                      style={{ flex: 1 }}
                    >
                      {t(m.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Current effective commission */}
              <div className="discount-current">
                <span className="discount-current-label">{t('agencies.currentCommission')}</span>
                <span className={`discount-badge ${cur?.source === 'auto' ? 'discount-auto' : 'discount-manual'}`}>
                  {cur?.commission_percent || 0}%
                  <span className="discount-source">
                    {t(cur?.source === 'auto' ? 'agencies.sourceAuto' : 'agencies.sourceManual')}
                  </span>
                </span>
                {cur?.source === 'auto' && cur?.spend_90d != null && (
                  <span className="discount-spend-info">
                    {t('agencies.spend90d', { amount: Math.round(cur.spend_90d).toLocaleString(currentLocale()) })}
                  </span>
                )}
              </div>

              {/* Per-day fixed history (auto tier shifts with the trailing 90-day spend) */}
              {data.daily && data.daily.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div className="field-label" style={{ marginBottom: 6 }}>{t('agencies.dailyHistory')}</div>
                  <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                    <table className="users-table discount-stages-table" style={{ minWidth: 0, tableLayout: 'auto' }}>
                      <thead><tr><th>{t('agencies.colDate')}</th><th>{t('agencies.colCommission')}</th><th>{t('agencies.colSource')}</th></tr></thead>
                      <tbody>
                        {data.daily.map((d: any) => (
                          <tr key={d.date}>
                            <td>{d.date.split('-').reverse().join('.')}</td>
                            <td><strong>{d.commission_percent}%</strong></td>
                            <td><span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                              {t(d.source === 'auto' ? 'agencies.sourceAuto' : 'agencies.sourceManual')}
                            </span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Manual stages table */}
              {data.stages && data.stages.length > 0 && (
                <table className="users-table discount-stages-table" style={{ minWidth: 0, tableLayout: 'auto' }}>
                  <thead>
                    <tr><th>{t('agencies.colEffectiveFrom')}</th><th>{t('agencies.colCommission')}</th><th>{t('agencies.colStatus')}</th><th></th></tr>
                  </thead>
                  <tbody>
                    {data.stages.map((stage: any) => (
                      <tr key={stage.id} className={stage.is_active ? 'stage-active' : 'stage-future'}>
                        <td>{stage.effective_from.split('-').reverse().join('.')}</td>
                        <td><strong>{stage.commission_percent}%</strong></td>
                        <td>
                          <span className={`discount-stage-status ${stage.is_active ? 'status-active' : 'status-future'}`}>
                            {t(stage.is_active ? 'agencies.stageActive' : 'agencies.stageFuture')}
                          </span>
                        </td>
                        <td>
                          <button className="users-action-btn users-action-delete" onClick={() => handleDeleteStage(stage.id)} title={t('agencies.deleteStage')}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Add manual stage */}
              <form onSubmit={handleAddStage} className="discount-add-form">
                <div className="discount-add-row">
                  <div className="discount-date-wrap">
                    <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} required className="discount-input discount-date-native" />
                    <span className="discount-date-display">
                      {newDate ? newDate.split('-').reverse().join('.') : <span style={{ color: 'var(--text-tertiary, #aaa)' }}>dd.mm.yyyy</span>}
                    </span>
                  </div>
                  <input type="number" min={0} max={50} step={0.5} value={newPercent} onChange={e => setNewPercent(e.target.value)}
                    placeholder="0-50%" required className="discount-input" style={{ width: '100px' }} />
                  <button type="submit" className="offices-btn offices-btn-primary" disabled={submitting}>
                    {t(submitting ? 'agencies.adding' : 'clients.fix')}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
        <div className="offices-modal-footer">
          <button className="offices-btn offices-btn-secondary" onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Main Page
// ============================================

const AgenciesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveAgency, setArchiveAgency] = useState<Agency | null>(null);
  const [archiveAction, setArchiveAction] = useState<'archive' | 'restore'>('archive');
  const [archiving, setArchiving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAgency, setDeleteAgency] = useState<Agency | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountAgency, setDiscountAgency] = useState<Agency | null>(null);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [commissionAgency, setCommissionAgency] = useState<Agency | null>(null);
  const [commissionMap, setCommissionMap] = useState<Record<string, { commission_percent: number; source: string; model_type: string }>>({});
  // Who this agency actually works for — the list its admins asked to see.
  const [clientsAgency, setClientsAgency] = useState<Agency | null>(null);
  const [agencyClients, setAgencyClients] = useState<Client[]>([]);
  const [agencyClientsLoading, setAgencyClientsLoading] = useState(false);

  const openClientsModal = async (agency: Agency) => {
    setClientsAgency(agency);
    setAgencyClients([]);
    setAgencyClientsLoading(true);
    try {
      const data = await clientsApi.list({ agency_id: agency.id, limit: 500 });
      setAgencyClients(data.clients || []);
    } catch {
      setAgencyClients([]);
    } finally {
      setAgencyClientsLoading(false);
    }
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchAgencies = useCallback(async () => {
    try {
      const data = await agenciesApi.list({ status: statusFilter || undefined });
      setAgencies(data.agencies);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  }, [statusFilter]);

  const fetchOffices = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/regional-offices`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOffices(data.offices || []);
      }
    } catch {}
  }, [getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAgencies(), fetchOffices()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAgencies, fetchOffices]);

  // Resolve commission + account model for the list columns.
  const loadCommissions = useCallback(async (list: Agency[]) => {
    const ids = list.map(a => String(a.id));
    if (!ids.length) { setCommissionMap({}); return; }
    try {
      const { results } = await commissionsApi.bulk('agency', ids);
      setCommissionMap(results || {});
    } catch { /* non-blocking */ }
  }, []);
  useEffect(() => { loadCommissions(agencies); }, [agencies, loadCommissions]);

  // Esc key to close modals
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showArchiveModal) { setShowArchiveModal(false); setArchiveAgency(null); }
        if (showCreateModal) setShowCreateModal(false);
        if (showEditModal) { setShowEditModal(false); setSelectedAgency(null); }
        if (showDiscountModal) { setShowDiscountModal(false); setDiscountAgency(null); }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showCreateModal, showEditModal, showArchiveModal, showDiscountModal]);

  const openArchiveModal = (agency: Agency, action: 'archive' | 'restore') => {
    setArchiveAgency(agency);
    setArchiveAction(action);
    setShowArchiveModal(true);
  };

  const handleArchiveRestore = async () => {
    if (!archiveAgency) return;
    setArchiving(true);
    try {
      if (archiveAction === 'archive') {
        await agenciesApi.archive(archiveAgency.id);
      } else {
        await agenciesApi.restore(archiveAgency.id);
      }
      fetchAgencies();
      setShowArchiveModal(false);
      setArchiveAgency(null);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteAgency) return;
    setDeleting(true);
    try {
      await agenciesApi.remove(deleteAgency.id);
      fetchAgencies();
      setShowDeleteModal(false);
      setDeleteAgency(null);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const filteredAgencies = agencies.filter(a => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!a.name.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('agencies.title')}</h1>
          <p className="header-subtitle">{t('agencies.subtitle')}</p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="info-box" style={{ marginBottom: '24px', borderLeftColor: '#e74c3c', background: 'rgba(231, 76, 60, 0.1)' }}>
          <strong>{t('clients.errorLabel')}</strong> {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>x</button>
        </div>
      )}

      <div className="url-lists-layout">
        {/* Left Panel */}
        <aside className="url-lists-panel">
          {/* Search */}
          <div className="panel-section">
            <div className="search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder={t('agencies.searchByName')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="panel-section">
            <label className="field-label">{t('agencies.colStatus')}</label>
            <select
              className="field-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">{t('billing.allStatuses')}</option>
              <option value="active">{t('agencies.stActive')}</option>
              <option value="archived">{t('agencies.stArchived')}</option>
            </select>
          </div>

          {/* Add Button */}
          <button className="add-list-btn" onClick={() => setShowCreateModal(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>{t('agencies.createAgency')}</span>
          </button>
        </aside>

        {/* Editor Area */}
        <div className="url-editor-panel">
          {loading ? (
            <div className="offices-loading">
              <div className="offices-spinner" />
            </div>
          ) : filteredAgencies.length === 0 ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11h20V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5z"/>
                </svg>
              </div>
              <h3>{t(searchQuery ? 'agencies.noMatching' : 'agencies.noneYet')}</h3>
              <p>{t(searchQuery ? 'agencies.tryDifferent' : 'agencies.createFirst')}</p>
              {!searchQuery && (
                <button className="editor-empty-btn" onClick={() => setShowCreateModal(true)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  {t('agencies.createAgency')}
                </button>
              )}
            </div>
          ) : (
            <div className="users-table-container agencies-table-wrap">
              <table className="users-table agencies-table">
                <thead>
                  <tr>
                    <th>{t('agencies.colAgency')}</th>
                    <th>{t('agencies.colOffice')}</th>
                    <th>{t('agencies.colManager')}</th>
                    <th>{t('agencies.colAdmin')}</th>
                    <th>{t('agencies.colUsers')}</th>
                    <th>{t('agencies.colClients')}</th>
                    <th>{t('agencies.colDiscount')}</th>
                    <th>{t('agencies.colCommission')}</th>
                    <th>{t('agencies.colAccounts')}</th>
                    <th>{t('agencies.colMarkup')}</th>
                    <th>{t('agencies.colBilling')}</th>
                    <th>{t('billing.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAgencies.map(agency => (
                      <tr key={agency.id}>
                        <td>
                          <div className="users-user-cell">
                            <div className="users-avatar" style={{ background: 'linear-gradient(135deg, #FF6B4A 0%, #E94B6C 50%, #8E3A9A 100%)' }}>
                              {agency.name.slice(0, 2).toUpperCase()}
                            </div>
                            <div className="users-user-info">
                              <span className="users-user-name">{agency.name}</span>
                            </div>
                          </div>
                        </td>
                        <td>{agency.office_name || '—'}</td>
                        <td>
                          {agency.manager_email ? (
                            <div className="users-user-info">
                              <span className="users-user-name" style={{ fontSize: '13px' }}>
                                {agency.manager_first_name && agency.manager_last_name
                                  ? `${agency.manager_first_name} ${agency.manager_last_name}`
                                  : agency.manager_email}
                              </span>
                              {agency.manager_first_name && (
                                <span className="users-user-email" style={{ fontSize: '11px' }}>{agency.manager_email}</span>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td>
                          {(agency.admins && agency.admins.length > 0) ? (
                            <div className="agencies-admins">
                              {agency.admins.map(a => (
                                <div key={a.id} className="users-user-info">
                                  <span className="users-user-name" style={{ fontSize: '13px' }}>
                                    {a.first_name && a.last_name
                                      ? `${a.first_name} ${a.last_name}`
                                      : a.email}
                                  </span>
                                  {a.first_name && (
                                    <span className="users-user-email" style={{ fontSize: '11px' }}>{a.email}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : '—'}
                        </td>
                        <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{agency.user_count}</td>
                        <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{agency.client_count}</td>
                        <td style={{ textAlign: 'center' }}>
                          {agency.discount && agency.discount.discount_percent > 0 ? (
                            <span className={`discount-badge ${agency.discount.source === 'auto' ? 'discount-auto' : 'discount-manual'}`}>
                              {agency.discount.discount_percent}%{' '}
                              {t(agency.discount.source === 'auto' ? 'agencies.sourceAuto' : 'agencies.sourceManual')}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {(() => {
                            const cm = commissionMap[String(agency.id)];
                            if (!cm) return '—';
                            return (
                              <span className={`discount-badge ${cm.source === 'auto' ? 'discount-auto' : 'discount-manual'}`}>
                                {cm.commission_percent}%{' '}
                                {t(cm.source === 'auto' ? 'agencies.sourceAuto' : 'agencies.sourceManual')}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {(() => {
                            const cm = commissionMap[String(agency.id)];
                            const own = (cm?.model_type || 'client_accounts') === 'own_accounts';
                            return (
                              <span
                                title={t(own ? 'agencies.modelOwn' : 'agencies.modelClient')}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, fontSize: 12, fontWeight: 800, color: '#fff', background: own ? '#7c3aed' : '#059669' }}
                              >
                                {own ? 'S' : 'C'}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                          {agency.default_client_markup > 0 ? `${agency.default_client_markup}%` : '—'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{
                            fontSize: '12px',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            background: agency.billing_model === 'postpaid' ? 'rgba(124, 58, 237, 0.1)' : 'rgba(5, 150, 105, 0.1)',
                            color: agency.billing_model === 'postpaid' ? '#7c3aed' : '#059669',
                            fontWeight: 600,
                          }}>
                            {t(agency.billing_model === 'postpaid' ? 'billing.bm_postpaid' : 'billing.bm_prepaid')}
                          </span>
                        </td>
                        <td>
                          <div className="users-actions">
                            <button
                              className="users-action-btn"
                              onClick={() => openClientsModal(agency)}
                              title={t('agencies.actClients')}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                              </svg>
                            </button>
                            <button
                              className="users-action-btn"
                              onClick={() => { setDiscountAgency(agency); setShowDiscountModal(true); }}
                              title={t('agencies.actDiscount')}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="19" y1="5" x2="5" y2="19"/>
                                <circle cx="6.5" cy="6.5" r="2.5"/>
                                <circle cx="17.5" cy="17.5" r="2.5"/>
                              </svg>
                            </button>
                            <button
                              className="users-action-btn"
                              onClick={() => { setCommissionAgency(agency); setShowCommissionModal(true); }}
                              title={t('agencies.actCommission')}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="1" x2="12" y2="23"/>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                              </svg>
                            </button>
                            <button
                              className="users-action-btn"
                              onClick={() => { setSelectedAgency(agency); setShowEditModal(true); }}
                              title={t('agencies.actEdit')}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            {agency.status === 'active' ? (
                              <button
                                className="users-action-btn users-action-delete"
                                onClick={() => openArchiveModal(agency, 'archive')}
                                title={t('agencies.actArchive')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="21 8 21 21 3 21 3 8"/>
                                  <rect x="1" y="3" width="22" height="5"/>
                                  <line x1="10" y1="12" x2="14" y2="12"/>
                                </svg>
                              </button>
                            ) : (
                              <button
                                className="users-action-btn users-action-activate"
                                onClick={() => openArchiveModal(agency, 'restore')}
                                title={t('agencies.actRestore')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M23 4v6h-6"/>
                                  <path d="M1 20v-6h6"/>
                                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                                </svg>
                              </button>
                            )}
                            {agency.status !== 'active' && user?.role === 'admin' && (
                              <button
                                className="users-action-btn users-action-delete"
                                onClick={() => { setDeleteAgency(agency); setShowDeleteModal(true); }}
                                title={t('agencies.actDelete')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  <line x1="10" y1="11" x2="10" y2="17"/>
                                  <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modals */}
        {showCreateModal && (
          <CreateAgencyModal
            offices={offices}
            onClose={() => setShowCreateModal(false)}
            onSuccess={fetchAgencies}
            getAuthHeaders={getAuthHeaders}
            currentUser={user}
          />
        )}
        {showEditModal && selectedAgency && (
          <EditAgencyModal
            agency={selectedAgency}
            offices={offices}
            onClose={() => { setShowEditModal(false); setSelectedAgency(null); }}
            onSuccess={fetchAgencies}
            getAuthHeaders={getAuthHeaders}
            currentUser={user}
          />
        )}

        {/* Clients of this agency — the list its admins asked for. Read-only:
            moving a client between agencies stays on the Clients page, so there
            is one place where that decision is made. */}
        {clientsAgency && (
          <div className="offices-modal-overlay" onClick={() => setClientsAgency(null)}>
            <div className="offices-modal" onClick={e => e.stopPropagation()}>
              <div className="offices-modal-header">
                <h2>{t('agencies.clientsOf', { agency: clientsAgency.name })}</h2>
                <button className="offices-modal-close" onClick={() => setClientsAgency(null)}>&times;</button>
              </div>
              <div className="offices-modal-body">
                {agencyClientsLoading ? (
                  <div className="agencies-clients-empty">{t('agencies.loadingDots')}</div>
                ) : agencyClients.length === 0 ? (
                  <div className="agencies-clients-empty">{t('agencies.noClientsYet')}</div>
                ) : (
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>{t('agencies.colClient')}</th>
                        <th>{t('agencies.colManagers')}</th>
                        <th style={{ textAlign: 'center' }}>{t('agencies.colCampaigns')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencyClients.map(c => (
                        <tr key={c.advertiser_id}>
                          <td>
                            <div className="users-user-info">
                              <span className="users-user-name" style={{ fontSize: '13px' }}>{c.display_name}</span>
                              <span className="users-user-email" style={{ fontSize: '11px' }}>{c.advertiser_id}</span>
                            </div>
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            {(c.managers && c.managers.length > 0)
                              ? c.managers.map(m => (
                                  m.first_name && m.last_name ? `${m.first_name} ${m.last_name}` : m.email
                                )).join(', ')
                              : '—'}
                          </td>
                          <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                            {c.campaigns_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Archive/Restore Confirmation Modal */}
        {showArchiveModal && archiveAgency && (
          <div className="users-modal-overlay" onClick={() => { setShowArchiveModal(false); setArchiveAgency(null); }}>
            <div className="users-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
              <div className="users-modal-header">
                <h2>{t(archiveAction === 'archive' ? 'agencies.archiveTitle' : 'agencies.restoreTitle')}</h2>
                <button className="users-modal-close" onClick={() => { setShowArchiveModal(false); setArchiveAgency(null); }}>&times;</button>
              </div>
              <div className="users-modal-body">
                <p style={{ fontSize: '15px', lineHeight: '1.6' }}>
                  <Trans
                    i18nKey={archiveAction === 'archive' ? 'agencies.archiveConfirm' : 'agencies.restoreConfirm'}
                    values={{ name: archiveAgency.name }}
                    components={{ b: <strong /> }}
                  />
                </p>
              </div>
              <div className="users-modal-footer">
                <button className="users-btn users-btn-secondary" onClick={() => { setShowArchiveModal(false); setArchiveAgency(null); }}>
                  {t('common.cancel')}
                </button>
                <button
                  className={`users-btn ${archiveAction === 'archive' ? 'users-btn-danger' : 'users-btn-primary'}`}
                  onClick={handleArchiveRestore}
                  disabled={archiving}
                  style={archiveAction === 'archive' ? { background: '#e74c3c', color: '#fff' } : undefined}
                >
                  {archiving
                    ? t(archiveAction === 'archive' ? 'agencies.archiving' : 'agencies.restoring')
                    : t(archiveAction === 'archive' ? 'agencies.archive' : 'agencies.restore')
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Permanent Delete Confirmation Modal */}
        {showDeleteModal && deleteAgency && (
          <div className="users-modal-overlay" onClick={() => { setShowDeleteModal(false); setDeleteAgency(null); }}>
            <div className="users-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
              <div className="users-modal-header">
                <h2>{t('agencies.deleteTitle')}</h2>
                <button className="users-modal-close" onClick={() => { setShowDeleteModal(false); setDeleteAgency(null); }}>&times;</button>
              </div>
              <div className="users-modal-body">
                <p style={{ fontSize: '15px', lineHeight: '1.6' }}>
                  <Trans
                    i18nKey="agencies.deleteConfirm"
                    values={{ name: deleteAgency.name }}
                    components={{ b: <strong />, s: <strong /> }}
                  />
                </p>
                <p style={{ fontSize: 12, color: '#6c757d' }}>
                  {t('agencies.deleteBlocked')}
                </p>
              </div>
              <div className="users-modal-footer">
                <button className="users-btn users-btn-secondary" onClick={() => { setShowDeleteModal(false); setDeleteAgency(null); }}>
                  {t('common.cancel')}
                </button>
                <button
                  className="users-btn users-btn-danger"
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{ background: '#e74c3c', color: '#fff' }}
                >
                  {t(deleting ? 'agencies.deleting' : 'agencies.deletePermanently')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Commission Stages Modal */}
        {showCommissionModal && commissionAgency && (
          <CommissionStagesModal
            agency={commissionAgency}
            onClose={() => { setShowCommissionModal(false); setCommissionAgency(null); loadCommissions(agencies); }}
          />
        )}

        {/* Discount Stages Modal */}
        {showDiscountModal && discountAgency && (
          <DiscountStagesModal
            agency={discountAgency}
            onClose={() => { setShowDiscountModal(false); setDiscountAgency(null); fetchAgencies(); }}
          />
        )}
      </div>
    </>
  );
};

export default AgenciesPage;
